'use client';

import React from 'react';

import { Box } from './box';
import { VStack, HStack } from './stack';
import { Text } from './text';
import { Spinner } from './spinner';
import { Progress } from './progress';
import { Skeleton } from './skeleton';

export type LoadingStateVariant = 'spinner' | 'skeleton' | 'progress' | 'dots' | 'minimal';
export type LoadingStateSize = 'sm' | 'md' | 'lg' | 'xl';
export type LoadingStatePreset = 'kpiCard' | 'statCard' | 'dataTable' | 'form' | 'page';

export interface LoadingStateProps {
  /**
   * The variant of the loading state
   */
  variant?: LoadingStateVariant;
  /**
   * Size of the loading indicator
   */
  size?: LoadingStateSize;
  /**
   * Whether it's full page or inline
   */
  fullPage?: boolean;
  /**
   * Whether to render as overlay (absolute positioned)
   */
  asOverlay?: boolean;
  /**
   * Preset configuration for common use cases
   */
  preset?: LoadingStatePreset;
  /**
   * Loading text to display
   */
  text?: string;
  /**
   * Progress value (0-100) for progress variant
   */
  progress?: number;
  /**
   * Number of skeleton rows for skeleton variant
   */
  skeletonRows?: number;
  /**
   * Whether to show skeleton avatar
   */
  showSkeletonAvatar?: boolean;
  /**
   * Custom height for full page mode
   */
  minHeight?: string;
  /**
   * Background color
   */
  backgroundColor?: string;
  /**
   * Loading indicator color
   */
  color?: string;
  /**
   * Blur background when overlay mode
   */
  blurBackground?: boolean;
  /**
   * i18n translations
   */
  translations?: {
    loading?: string;
    pleaseWait?: string;
    loadingData?: string;
    processingRequest?: string;
    almostReady?: string;
  };
}

// Size mappings
const spinnerSizes = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
};

const textSizes = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
};

// Default translations
const defaultTranslations = {
  loading: 'Loading',
  pleaseWait: 'Please wait...',
  loadingData: 'Loading data',
  processingRequest: 'Processing request',
  almostReady: 'Almost ready',
};

// Preset configurations
const presetConfigs: Record<LoadingStatePreset, Partial<LoadingStateProps>> = {
  kpiCard: {
    variant: 'minimal',
    size: 'sm',
    text: 'Loading...',
    minHeight: '100px',
  },
  statCard: {
    variant: 'minimal',
    size: 'sm',
    text: 'Loading...',
    minHeight: '100px',
  },
  dataTable: {
    variant: 'skeleton',
    skeletonRows: 5,
    minHeight: '300px',
  },
  form: {
    variant: 'spinner',
    size: 'md',
    text: 'Processing...',
  },
  page: {
    variant: 'spinner',
    size: 'lg',
    fullPage: true,
    text: 'Loading page...',
  },
};

export function LoadingState({
  variant = 'spinner',
  size = 'md',
  fullPage = false,
  asOverlay = false,
  preset,
  text,
  progress = 0,
  skeletonRows = 3,
  showSkeletonAvatar = false,
  minHeight = '400px',
  backgroundColor = fullPage ? '#F8F9FA' : 'transparent',
  color = '#0066CC',
  blurBackground = false,
  translations = {},
}: LoadingStateProps) {
  // Apply preset configuration if provided
  const presetConfig = preset ? presetConfigs[preset] : {};
  const finalConfig = {
    variant,
    size,
    fullPage,
    asOverlay,
    text,
    progress,
    skeletonRows,
    showSkeletonAvatar,
    minHeight,
    backgroundColor,
    color,
    blurBackground,
    ...presetConfig,
  };
  // Use final config values
  const {
    variant: finalVariant,
    size: finalSize,
    text: finalText,
    minHeight: finalMinHeight,
    backgroundColor: finalBgColor,
    color: finalColor,
    skeletonRows: finalSkeletonRows,
    showSkeletonAvatar: finalShowAvatar,
    fullPage: finalFullPage,
    asOverlay: finalAsOverlay,
    blurBackground: finalBlurBg,
  } = finalConfig;

  // Merge translations with defaults
  const t = { ...defaultTranslations, ...translations };

  // Get loading text based on progress
  const getLoadingText = () => {
    if (finalText) return finalText;
    if (progress > 80) return t.almostReady;
    if (progress > 50) return t.processingRequest;
    if (progress > 0) return t.loadingData;
    return t.pleaseWait;
  };

  // Render minimal variant
  const renderMinimal = () => (
    <Text fontSize={textSizes[finalSize]} color="#6C757D">
      {getLoadingText()}
    </Text>
  );

  // Render spinner variant
  const renderSpinner = () => (
    <VStack gap={3}>
      <Spinner
        size={spinnerSizes[finalSize] as 'xs' | 'sm' | 'md' | 'lg' | 'xl'}
        color={finalColor}
        thickness="3px"
      />
      {(finalText || finalFullPage) && (
        <Text fontSize={textSizes[finalSize]} color="#6C757D">
          {finalText || t.loading}
        </Text>
      )}
    </VStack>
  );

  // Render skeleton variant
  const renderSkeleton = () => (
    <VStack gap={3} width="100%" align="stretch">
      {finalShowAvatar && (
        <HStack gap={3}>
          <Skeleton
            variant="circular"
            width={finalSize === 'sm' ? '40px' : finalSize === 'lg' ? '80px' : '60px'}
            height={finalSize === 'sm' ? '40px' : finalSize === 'lg' ? '80px' : '60px'}
          />
          <VStack flex="1" align="stretch" gap={2}>
            <Skeleton height="20px" width="40%" />
            <Skeleton height="16px" width="60%" />
          </VStack>
        </HStack>
      )}
      {Array.from({ length: finalSkeletonRows }).map((_, index) => (
        <Skeleton
          key={index}
          height={finalSize === 'sm' ? '16px' : finalSize === 'lg' ? '24px' : '20px'}
          width={index === finalSkeletonRows - 1 ? '70%' : '100%'}
        />
      ))}
    </VStack>
  );

  // Render progress variant
  const renderProgress = () => (
    <VStack gap={3} width="100%" maxWidth="300px">
      <Progress value={progress} size={finalSize} color={finalColor} />
      <Text fontSize={textSizes[finalSize]} color="#6C757D">
        {getLoadingText()} ({progress}%)
      </Text>
    </VStack>
  );

  // Render dots variant
  const renderDots = () => {
    const dotSize =
      finalSize === 'sm'
        ? '8px'
        : finalSize === 'lg'
          ? '16px'
          : finalSize === 'xl'
            ? '20px'
            : '12px';

    return (
      <VStack gap={3}>
        <HStack gap={2}>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              width={dotSize}
              height={dotSize}
              borderRadius="full"
              backgroundColor={finalColor}
              animation={`pulse 1.4s ease-in-out infinite`}
              animationDelay={`${index * 0.16}s`}
            />
          ))}
        </HStack>
        {(finalText || finalFullPage) && (
          <Text fontSize={textSizes[finalSize]} color="#6C757D">
            {finalText || t.loading}
          </Text>
        )}
      </VStack>
    );
  };

  // Render loading content based on variant
  const renderContent = () => {
    switch (finalVariant) {
      case 'minimal':
        return renderMinimal();
      case 'spinner':
        return renderSpinner();
      case 'skeleton':
        return renderSkeleton();
      case 'progress':
        return renderProgress();
      case 'dots':
        return renderDots();
      default:
        return renderSpinner();
    }
  };

  // Container styles
  const containerStyles = finalFullPage
    ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: finalMinHeight,
        width: '100%',
        backgroundColor: finalBgColor,
      }
    : finalAsOverlay
      ? {
          position: 'absolute' as const,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: finalBlurBg ? 'rgba(255, 255, 255, 0.8)' : finalBgColor,
          backdropFilter: finalBlurBg ? 'blur(4px)' : undefined,
          zIndex: 10,
        }
      : {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: preset === 'kpiCard' || preset === 'statCard' ? finalMinHeight : undefined,
        };

  return (
    <Box {...containerStyles}>
      {renderContent()}

      {/* Dots animation is handled inline in renderDots */}
    </Box>
  );
}

// Export with display name for debugging
LoadingState.displayName = 'LoadingState';
